import React, { useState } from 'react';
import { MessageCircle, Send, MapPin, Phone, Mail, Award, Clock, CheckCircle, ArrowRight, ShieldCheck, Package, Headphones } from 'lucide-react';

function ContactPage({ setCurrentPage }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitted(true);
    setTimeout(() => {
      setFormData({ name: '', email: '', phone: '', message: '' });
      setIsSubmitted(false);
    }, 3000);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div style={{ background: '#FFF9F0', minHeight: '100vh' }}>
      {/* Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, #FF8A00 0%, #FF6A00 100%)',
        color: 'white',
        padding: '6rem 2rem 4rem',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative Elements */}
        <div style={{
          position: 'absolute',
          top: '-50px',
          right: '-50px',
          width: '300px',
          height: '300px',
          background: 'rgba(255, 193, 7, 0.1)',
          borderRadius: '50%',
          filter: 'blur(60px)'
        }}></div>
        <div style={{
          position: 'absolute',
          bottom: '-50px',
          left: '-50px',
          width: '250px',
          height: '250px',
          background: 'rgba(255, 255, 255, 0.08)',
          borderRadius: '50%',
          filter: 'blur(50px)'
        }}></div>

        <div style={{ maxWidth: '800px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{
            display: 'inline-block',
            background: 'rgba(255, 255, 255, 0.15)',
            padding: '0.5rem 1.5rem',
            borderRadius: '50px',
            marginBottom: '1.5rem',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            backdropFilter: 'blur(10px)'
          }}>
            <span style={{ fontSize: '0.95rem', fontWeight: '600', letterSpacing: '1px' }}>
              📞 CONTACT US
            </span>
          </div>

          <h1 style={{ 
            fontSize: 'clamp(2.5rem, 5vw, 3.5rem)', 
            fontWeight: 'bold', 
            marginBottom: '1.5rem',
            lineHeight: 1.2,
            letterSpacing: '-1px'
          }}>
            Get in Touch with{' '}
            <span style={{ 
              background: 'linear-gradient(120deg, #FFD180, #FFFFFF)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              display: 'inline-block'
            }}>Summer Beat</span>
          </h1>
          
          <p style={{ 
            fontSize: '1.25rem', 
            opacity: 0.95,
            lineHeight: 1.7,
            maxWidth: '700px',
            margin: '0 auto'
          }}>
            Have questions about our products? Need assistance with your purchase? We're here to help with all your home appliance needs!
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section style={{ padding: '5rem 2rem' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div style={{ 
            display: 'grid',
            gap: '3rem',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))'
          }}>
            {/* Contact Form */}
            <div style={{
              background: 'white',
              padding: '3rem',
              borderRadius: '25px',
              boxShadow: '0 20px 60px rgba(255, 138, 0, 0.12)',
              border: '1px solid rgba(255, 138, 0, 0.15)',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Success Message */}
              {isSubmitted && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(255, 255, 255, 0.98)',
                  backdropFilter: 'blur(10px)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10,
                  animation: 'fadeIn 0.3s ease-out'
                }}>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #FF8A00, #FF6A00)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '1.5rem'
                  }}>
                    <CheckCircle size={40} style={{ color: 'white' }} />
                  </div>
                  <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#FF8A00', marginBottom: '0.5rem' }}>
                    Message Sent!
                  </h3>
                  <p style={{ color: '#666666', fontSize: '1.05rem', textAlign: 'center' }}>
                    We'll get back to you within 24 hours
                  </p>
                </div>
              )}

              <div style={{ marginBottom: '2rem' }}>
                <h2 style={{ 
                  fontSize: '1.75rem', 
                  fontWeight: 'bold', 
                  marginBottom: '0.75rem',
                  color: '#1A1A1A'
                }}>
                  Send us a Message
                </h2>
                <p style={{ color: '#666666', fontSize: '1rem' }}>
                  Fill out the form and our team will respond shortly
                </p>
              </div>

              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '0.95rem', 
                    fontWeight: '600', 
                    color: '#1A1A1A', 
                    marginBottom: '0.5rem' 
                  }}>
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '0.875rem 1rem',
                      border: '2px solid rgba(255, 138, 0, 0.3)',
                      borderRadius: '12px',
                      outline: 'none',
                      transition: 'all 0.2s',
                      fontSize: '1rem',
                      color: '#1A1A1A',
                      background: 'rgba(255, 138, 0, 0.05)'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#FF8A00';
                      e.target.style.background = 'white';
                      e.target.style.boxShadow = '0 0 0 3px rgba(255, 138, 0, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(255, 138, 0, 0.3)';
                      e.target.style.background = 'rgba(255, 138, 0, 0.05)';
                      e.target.style.boxShadow = 'none';
                    }}
                    placeholder="Enter your full name"
                    required
                  />
                </div>

                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '0.95rem', 
                    fontWeight: '600', 
                    color: '#1A1A1A', 
                    marginBottom: '0.5rem' 
                  }}>
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '0.875rem 1rem',
                      border: '2px solid rgba(255, 138, 0, 0.3)',
                      borderRadius: '12px',
                      outline: 'none',
                      transition: 'all 0.2s',
                      fontSize: '1rem',
                      color: '#1A1A1A',
                      background: 'rgba(255, 138, 0, 0.05)'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#FF8A00';
                      e.target.style.background = 'white';
                      e.target.style.boxShadow = '0 0 0 3px rgba(255, 138, 0, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(255, 138, 0, 0.3)';
                      e.target.style.background = 'rgba(255, 138, 0, 0.05)';
                      e.target.style.boxShadow = 'none';
                    }}
                    placeholder="Enter your email"
                    required
                  />
                </div>

                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '0.95rem', 
                    fontWeight: '600', 
                    color: '#1A1A1A', 
                    marginBottom: '0.5rem' 
                  }}>
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '0.875rem 1rem',
                      border: '2px solid rgba(255, 138, 0, 0.3)',
                      borderRadius: '12px',
                      outline: 'none',
                      transition: 'all 0.2s',
                      fontSize: '1rem',
                      color: '#1A1A1A',
                      background: 'rgba(255, 138, 0, 0.05)'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#FF8A00';
                      e.target.style.background = 'white';
                      e.target.style.boxShadow = '0 0 0 3px rgba(255, 138, 0, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(255, 138, 0, 0.3)';
                      e.target.style.background = 'rgba(255, 138, 0, 0.05)';
                      e.target.style.boxShadow = 'none';
                    }}
                    placeholder="Enter your phone number"
                    required
                  />
                </div>

                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '0.95rem', 
                    fontWeight: '600', 
                    color: '#1A1A1A', 
                    marginBottom: '0.5rem' 
                  }}>
                    Your Message *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={6}
                    style={{
                      width: '100%',
                      padding: '0.875rem 1rem',
                      border: '2px solid rgba(255, 138, 0, 0.3)',
                      borderRadius: '12px',
                      outline: 'none',
                      transition: 'all 0.2s',
                      resize: 'vertical',
                      fontSize: '1rem',
                      color: '#1A1A1A',
                      background: 'rgba(255, 138, 0, 0.05)'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#FF8A00';
                      e.target.style.background = 'white';
                      e.target.style.boxShadow = '0 0 0 3px rgba(255, 138, 0, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(255, 138, 0, 0.3)';
                      e.target.style.background = 'rgba(255, 138, 0, 0.05)';
                      e.target.style.boxShadow = 'none';
                    }}
                    placeholder="Tell us about your inquiry or product needs..."
                    required
                  ></textarea>
                </div>

                <button
                  type="submit"
                  style={{
                    width: '100%',
                    background: 'linear-gradient(135deg, #FF8A00 0%, #FF6A00 100%)',
                    color: 'white',
                    padding: '1rem 0',
                    borderRadius: '12px',
                    fontWeight: '700',
                    fontSize: '1.05rem',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    boxShadow: '0 8px 20px rgba(255, 138, 0, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 12px 30px rgba(255, 138, 0, 0.4)';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 8px 20px rgba(255, 138, 0, 0.3)';
                  }}
                >
                  Send Message
                  <Send size={18} />
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
              {/* Company Info Card */}
              <div style={{
                background: 'linear-gradient(135deg, #FF8A00 0%, #FF6A00 100%)',
                color: 'white',
                padding: '2.5rem',
                borderRadius: '20px',
                boxShadow: '0 12px 30px rgba(255, 138, 0, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                  <Award size={32} />
                  <div>
                    <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>
                      ISO 9001:2015 CERTIFIED
                    </h3>
                    <p style={{ opacity: 0.9, fontSize: '0.9rem' }}>Premium Quality Home Appliances</p>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                    <MapPin size={20} style={{ marginTop: '2px' }} />
                    <div>
                      <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>Address</div>
                      <div style={{ opacity: 0.9, lineHeight: 1.6, fontSize: '0.95rem' }}>
                        Khasra No.1160, Sihani Gate<br />
                        Meerut Road, Ghaziabad<br />
                        Uttar Pradesh – 201001, India
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <Mail size={20} />
                    <div>
                      <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>Email</div>
                      <div style={{ opacity: 0.9, fontSize: '0.95rem' }}>
                        summerbeathomeappliance@gmail.com
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <Clock size={20} />
                    <div>
                      <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>Business Hours</div>
                      <div style={{ opacity: 0.9, fontSize: '0.95rem' }}>
                        Monday - Saturday: 9 AM - 7 PM
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => window.open('https://maps.google.com', '_blank')}
                  style={{
                    width: '100%',
                    background: 'white',
                    color: '#FF8A00',
                    padding: '0.9rem 0',
                    borderRadius: '12px',
                    fontWeight: '700',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.15)',
                    marginTop: '2rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.2)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.15)';
                  }}
                >
                  📍 View on Google Maps
                </button>
              </div>

              {/* Support Card */}
              <div style={{
                background: 'white',
                padding: '2rem',
                borderRadius: '20px',
                boxShadow: '0 10px 25px rgba(255, 138, 0, 0.1)',
                border: '1px solid rgba(255, 138, 0, 0.15)'
              }}>
                <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1A1A1A', marginBottom: '1rem' }}>
                  Customer Support
                </h3>
                <p style={{ color: '#666666', marginBottom: '1.25rem' }}>
                  Our support team is available to help with product inquiries, warranty claims, and technical assistance.
                </p>
                <div style={{ display: 'grid', gap: '0.85rem' }}>
                  {[
                    { type: 'Product Inquiries', contact: '+91-XXXXXXXXXX' },
                    { type: 'Technical Support', contact: '+91-XXXXXXXXXX' },
                    { type: 'Warranty Claims', contact: '+91-XXXXXXXXXX' },
                    { type: 'Dealer Enquiries', contact: '+91-XXXXXXXXXX' },
                  ].map((support, i) => (
                    <div key={i} style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      padding: '0.9rem 1rem', 
                      background: '#FFF9F0', 
                      borderRadius: '12px',
                      border: '1px solid rgba(255, 138, 0, 0.15)'
                    }}>
                      <span style={{ fontWeight: '600', color: '#1A1A1A', fontSize: '0.95rem' }}>{support.type}</span>
                      <span style={{ fontSize: '0.9rem', color: '#FF8A00', fontWeight: '500' }}>{support.contact}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick Links Card */}
              <div style={{
                background: 'linear-gradient(135deg, #FFF9F0 0%, #FFECB3 100%)',
                padding: '2rem',
                borderRadius: '20px',
                border: '1px solid rgba(255, 138, 0, 0.2)'
              }}>
                <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1A1A1A', marginBottom: '1rem' }}>
                  Quick Links
                </h3>
                <p style={{ color: '#666666', marginBottom: '1.25rem' }}>
                  Explore our range of products or learn more about our company.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  <button
                    onClick={() => setCurrentPage('products')}
                    style={{
                      background: 'white',
                      color: '#1A1A1A',
                      padding: '0.9rem 1.25rem',
                      borderRadius: '12px',
                      fontWeight: '600',
                      border: '2px solid rgba(255, 138, 0, 0.3)',
                      cursor: 'pointer',
                      transition: 'all 0.3s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'translateX(5px)';
                      e.currentTarget.style.borderColor = '#FF8A00';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 138, 0, 0.15)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'translateX(0)';
                      e.currentTarget.style.borderColor = 'rgba(255, 138, 0, 0.3)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Package size={18} />
                      Browse Products
                    </span>
                    <ArrowRight size={18} />
                  </button>

                  <button
                    onClick={() => setCurrentPage('about')}
                    style={{
                      background: 'white',
                      color: '#1A1A1A',
                      padding: '0.9rem 1.25rem',
                      borderRadius: '12px',
                      fontWeight: '600',
                      border: '2px solid rgba(255, 138, 0, 0.3)',
                      cursor: 'pointer',
                      transition: 'all 0.3s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'translateX(5px)';
                      e.currentTarget.style.borderColor = '#FF8A00';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 138, 0, 0.15)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'translateX(0)';
                      e.currentTarget.style.borderColor = 'rgba(255, 138, 0, 0.3)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <ShieldCheck size={18} />
                      About Summer Beat
                    </span>
                    <ArrowRight size={18} />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Assurance Banner */}
          <div style={{
            marginTop: '4rem',
            padding: '2.5rem',
            background: 'linear-gradient(135deg, #1E88E5 0%, #0D47A1 100%)',
            color: 'white',
            borderRadius: '20px',
            boxShadow: '0 15px 40px rgba(30, 136, 229, 0.3)',
            textAlign: 'center'
          }}>
            <h3 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '1rem' }}>
              Our Quality Promise
            </h3>
            <p style={{ fontSize: '1.15rem', opacity: 0.95, maxWidth: '800px', margin: '0 auto', lineHeight: 1.7, marginBottom: '1.5rem' }}>
              At Summer Beat, we stand behind every product we make. Enjoy premium quality, affordable pricing, 
              and excellent after-sales service with every purchase.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '3rem', flexWrap: 'wrap', marginTop: '2rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <ShieldCheck size={24} />
                <span style={{ fontWeight: '600' }}>1 Year Warranty</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <Headphones size={24} />
                <span style={{ fontWeight: '600' }}>24/7 Support</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <Award size={24} />
                <span style={{ fontWeight: '600' }}>ISO Certified</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0 }
          to { opacity: 1 }
        }
      `}</style>
    </div>
  );
}

export default ContactPage;